import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: BASE_URL });

export function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Deduplicate concurrent refresh calls — all 401s share one refresh request.
let refreshPromise = null;

async function doRefresh() {
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) throw new Error('No refresh token');
  const res = await axios.post(`${BASE_URL}/api/auth/refresh`, { refreshToken: refresh });
  localStorage.setItem('accessToken', res.data.accessToken);
  if (res.data.refreshToken) {
    localStorage.setItem('refreshToken', res.data.refreshToken);
  }
  return res.data.accessToken;
}

// ── Request interceptor — attach JWT + active currency ───────────────────────
api.interceptors.request.use(async config => {
  let token = localStorage.getItem('accessToken');

  // Proactively refresh an expired token before the request goes out, avoiding
  // a guaranteed 401 round-trip when the access token has just expired.
  if (token && isTokenExpired(token)) {
    try {
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
      }
      token = await refreshPromise;
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      if (window.location.pathname !== '/account') {
        window.location.href = '/account';
      }
      return Promise.reject(new Error('Session expired'));
    }
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Forward the user's currency preference so the backend can convert amounts.
  // Reads directly from localStorage to avoid a circular Context dependency.
  try {
    const stored = localStorage.getItem('currencyPreference');
    const currency = stored ? JSON.parse(stored).currency : 'KES';
    if (currency) config.headers['X-Currency'] = currency;
  } catch {
    config.headers['X-Currency'] = 'KES';
  }

  return config;
});

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  res => {
    // Guard: if the server responded with 2xx but the body is HTML instead of
    // JSON, it means a proxy/nginx SPA-fallback fired (common when a route is
    // mis-configured and nginx returns index.html with status 200).
    const contentType = res.headers?.['content-type'] ?? '';
    if (contentType.includes('text/html')) {
      const error = new Error(
        `API at "${res.config?.url}" returned HTML instead of JSON. ` +
        `This usually means the request URL is wrong or the backend service is down. ` +
        `Check nginx routing and ensure the service is running.`
      );
      error.name = 'HtmlResponseError';
      error.response = res;
      error.isHtmlResponse = true;
      return Promise.reject(error);
    }
    return res;
  },
  async err => {
    const originalRequest = err.config;

    // ── 401 → try silent token refresh ──────────────────────────────────────
    if (err.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const hadToken = !!localStorage.getItem('accessToken') || !!localStorage.getItem('refreshToken');
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Redirect to login when the user had tokens (standard session-expiry
        // flow) OR when the failing request targeted an admin/management path
        // (catches the case where a user lands on an admin page without any token).
        const isProtectedPath =
          originalRequest.url?.includes('/admin/') ||
          originalRequest.url?.includes('/management/');
        if ((hadToken || isProtectedPath) && window.location.pathname !== '/account') {
          window.location.href = '/account';
        }
      }
    }

    // ── Normalise error message for consumers ────────────────────────────────
    if (err.response?.data) {
      err.displayMessage = err.response.data.message
        ?? err.response.data.error
        ?? `Request failed with status ${err.response.status}`;
    } else if (err.isHtmlResponse) {
      err.displayMessage =
        'The server returned an unexpected response. ' +
        'Please check your connection or contact support.';
    } else if (!err.response) {
      err.displayMessage =
        'Cannot reach the server. Please check your internet connection.';
    } else {
      err.displayMessage = `Request failed with status ${err.response?.status ?? 'unknown'}.`;
    }

    return Promise.reject(err);
  }
);

export default api;
